import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { destinationApresConnexion } from '../../utils/redirectionConnexion';
import { KaneaLogo } from '../../components/Common/KaneaLogo';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import AuthBrandCanvas from '../../components/Auth/AuthBrandCanvas';


// hooks
import useFormEvents from '../../hooks/useFormEvents';
import usePageTitle from '../../hooks/usePageTitle';

// styles
import '../../styles/auth.css';

interface IFormProps {
  email: string;
  password: string;
}

const SigninScreen: React.FC = () => {
  usePageTitle('Connexion — KaneaBourse');
  const navigate = useNavigate();
  const location = useLocation();
  const { onlyEmail } = useFormEvents();
  const { login } = useAuth();

  const [formValues, setFormValues] = useState<IFormProps>({ email: '', password: '' });
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [motDePasseVisible, setMotDePasseVisible] = useState<boolean>(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formValues.email)) {
      setError('Veuillez entrer une adresse email valide.');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('username', formValues.email);
      formData.append('password', formValues.password);

      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Identifiants invalides. Veuillez réessayer.');
        } else {
          // 403 : compte suspendu ou email non vérifié — on affiche le message du serveur
          try {
            const errData = await response.json();
            setError(errData.detail || 'Connexion refusée. Veuillez contacter le support.');
          } catch {
            setError('Connexion refusée. Veuillez contacter le support.');
          }
        }
        return;
      }

      const data = await response.json();
      login(data.access_token);
      navigate(destinationApresConnexion(location.search));
    } catch (err) {
      setError('Erreur réseau. Vérifiez votre connexion et réessayez.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-root">
      <div className="auth-panel">

        {/* ── Panneau gauche branding ── */}
        <div className="auth-brand-panel">
          {/* Animation interactive : réseau de particules + spotlight souris */}
          <AuthBrandCanvas />
          <div className="auth-brand-spotlight" />

          <div className="auth-brand-logo">
            <div className="auth-brand-logo-img-wrap">
              <KaneaLogo variant="mark" title={null} />
            </div>
            <span className="auth-brand-name">KaneaBourse</span>
            <div className="auth-brand-logo-dot" />
          </div>

          <div className="auth-brand-body">
            <h2>
              N'investissez plus sur des <em>rumeurs</em>
            </h2>
            <p>
              KaneaBourse analyse froidement les bilans officiels BRVM —
              14 modèles de valorisation, indicateurs techniques, Nia votre copilote en français.
              Analysez ici, passez vos ordres chez votre SGI.
            </p>
            <div className="auth-brand-stats">
              <div>
                <div className="auth-brand-stat-value">10s</div>
                <div className="auth-brand-stat-label">Pour décider</div>
              </div>
              <div>
                <div className="auth-brand-stat-value">48</div>
                <div className="auth-brand-stat-label">Sociétés suivies</div>
              </div>
              <div>
                <div className="auth-brand-stat-value">24/7</div>
                <div className="auth-brand-stat-label">Copilote Nia</div>
              </div>
            </div>
          </div>

          <div className="auth-brand-footer">
            © {new Date().getFullYear()} KaneaBourse · Données EOD BRVM
          </div>
        </div>

        {/* ── Panneau droit formulaire ── */}
        <div className="auth-form-panel">
          <div className="auth-form-inner">

            {/* ── Logo Mobile ── */}
            <div className="auth-mobile-logo">
              <KaneaLogo variant="mark" title={null} />
              <span className="auth-mobile-logo-text">KaneaBourse</span>
            </div>

            <div className="auth-form-header">
              <h1>Bienvenue 👋</h1>
              <p>Connectez-vous pour accéder à vos outils d'analyse.</p>
            </div>

            {error && (
              <div className="auth-error">
                <span>⚠</span> {error}
              </div>
            )}

            <form noValidate onSubmit={handleSubmit}>

              <div className="auth-field">
                <label htmlFor="email">Adresse email</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formValues.email}
                  onChange={handleChange}
                  onKeyDown={onlyEmail}
                  placeholder="vous@exemple.com"
                  autoComplete="email"
                />
              </div>

              <div className="auth-field">
                <label htmlFor="password">
                  Mot de passe
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="password"
                    type={motDePasseVisible ? 'text' : 'password'}
                    name="password"
                    value={formValues.password}
                    onChange={handleChange}
                    placeholder="Votre mot de passe"
                    autoComplete="current-password"
                    required
                    style={{ paddingRight: 96 }}
                  />
                  <button
                    type="button"
                    onClick={() => setMotDePasseVisible((v) => !v)}
                    aria-pressed={motDePasseVisible}
                    style={{
                      position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                      minHeight: 36, padding: '0 10px', border: 'none', background: 'transparent',
                      color: '#6C3FC4', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    {motDePasseVisible ? 'Masquer' : 'Afficher'}
                  </button>
                </div>
                <Link to="/members/forgot-password" className="auth-forgot-link">
                  Mot de passe oublié ?
                </Link>
              </div>

              <button
                type="submit"
                className="auth-btn-primary"
                disabled={loading}
                style={{ marginTop: 8 }}
              >
                {loading ? 'Connexion…' : 'Se connecter →'}
              </button>

              <Link to="/members/signup" className="auth-btn-secondary">
                Créer un compte gratuitement
              </Link>

              <div className="auth-form-link">
                <Link to="/home">← Retour à l'accueil</Link>
              </div>

            </form>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SigninScreen;
